/**
 * RunTestModal.js
 *
 * Pre-test configuration modal shown before running a single ticket test
 * or batch test. Lets the user:
 *   1. Select KB articles to inject into the agent simulation prompt.
 *   2. Paste (or write) the agent system prompt.
 *
 * Props:
 *   isOpen          boolean  — show/hide
 *   onClose         fn()     — cancel handler
 *   onConfirm       fn({ kb_articles, agent_system_prompt }) — run handler
 *   mode            'single' | 'batch'
 *   ticketSubject   string   — shown in header when mode='single'
 *   availableKbArticles  Array<{id, title, body}> — articles from the KB picker
 */

import React, { useState, useEffect } from 'react';
import { useApi } from './hooks/useApi';

const STARTER_PROMPTS = [
  {
    label: 'Generic support agent (default)',
    value: '',
  },
  {
    label: 'SaaS technical support',
    value:
      'You are a technical support specialist for a SaaS platform. ' +
      'Help customers troubleshoot technical issues step by step. ' +
      'For billing or account questions, gather details and escalate to the billing team if needed. ' +
      'Always be concise, professional, and solution-focused.',
  },
  {
    label: 'E-commerce / returns agent',
    value:
      'You are a customer support agent for an e-commerce store. ' +
      'You handle order inquiries, returns, refunds, and shipping issues. ' +
      'Refunds are processed within 5-7 business days. ' +
      'For damaged or incorrect items, always offer a replacement first before a refund. ' +
      'Escalate fraud concerns to the security team immediately.',
  },
  {
    label: 'Subscription / billing focused',
    value:
      'You are a billing support specialist. ' +
      'Help customers understand charges, update payment methods, apply promo codes, ' +
      'and manage plan upgrades or cancellations. ' +
      'Always confirm the customer\'s account email before making any changes. ' +
      'For refund requests over $200, escalate to a senior billing agent.',
  },
];

const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/zendesk`;

const RunTestModal = ({
  isOpen,
  onClose,
  onConfirm,
  mode = 'single',
  ticketSubject = '',
  availableKbArticles = [],
}) => {
  const { authFetch } = useApi();
  const [selectedArticleIds, setSelectedArticleIds] = useState([]);
  const [agentPrompt, setAgentPrompt] = useState('');
  const [starterTemplate, setStarterTemplate] = useState('');
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [kbArticles, setKbArticles] = useState(availableKbArticles);
  const [activeTab, setActiveTab] = useState('kb'); // 'kb' | 'prompt'
  const [collections, setCollections] = useState([]);
  const [newCollName, setNewCollName] = useState('');
  const [showNewColl, setShowNewColl] = useState(false);
  const [savingColl, setSavingColl] = useState(false);

  // Fetch KB articles from the backend when the modal opens.
  // Dependency array intentionally omits availableKbArticles — it's a new array
  // reference on every render, which would cause an infinite refetch loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    // Reset state each time the modal opens
    setSelectedArticleIds([]);
    setAgentPrompt('');
    setActiveTab('kb');

    if (availableKbArticles && availableKbArticles.length > 0) {
      setKbArticles(availableKbArticles);
      return;
    }
    setLoadingArticles(true);
    (async () => {
      try {
        const [artData, collData] = await Promise.all([
          authFetch('/api/kb/articles'),
          authFetch('/api/kb/collections').catch(() => ({ collections: [] })),
        ]);
        const articles = (artData.articles || []).map(a => ({
          ...a,
          body: a.body_text || a.body || '',
        }));
        setKbArticles(articles);
        setCollections(collData.collections || []);
      } catch {
        setKbArticles([]);
      } finally {
        setLoadingArticles(false);
      }
    })();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply starter template to textarea
  useEffect(() => {
    if (starterTemplate !== undefined) setAgentPrompt(starterTemplate);
  }, [starterTemplate]);

  if (!isOpen) return null;

  const toggleArticle = (id) => {
    const sid = String(id);
    setSelectedArticleIds(prev =>
      prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]
    );
  };

  const selectAll = () => setSelectedArticleIds(kbArticles.map(a => String(a.id)));
  const clearAll = () => setSelectedArticleIds([]);

  const applyCollection = (coll) => {
    setSelectedArticleIds((coll.article_ids || []).map(String));
  };

  const saveAsCollection = async () => {
    if (!newCollName.trim() || selectedArticleIds.length === 0) return;
    setSavingColl(true);
    try {
      const data = await authFetch('/api/kb/collections', {
        method: 'POST',
        body: JSON.stringify({ name: newCollName.trim(), article_ids: selectedArticleIds.map(String) }),
      });
      if (data.collection) {
        setCollections(prev => [...prev, data.collection]);
        setNewCollName('');
        setShowNewColl(false);
      }
    } catch (e) {
      alert('Could not save collection');
    } finally {
      setSavingColl(false);
    }
  };

  const deleteCollection = async (collId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this collection?')) return;
    try {
      await authFetch(`/api/kb/collections/${collId}`, { method: 'DELETE' });
      setCollections(prev => prev.filter(c => c.id !== collId));
    } catch {}
  };

  const handleConfirm = () => {
    const selectedArticles = kbArticles.filter(a =>
      selectedArticleIds.includes(String(a.id))
    );
    onConfirm({
      kb_articles: selectedArticles.length > 0 ? selectedArticles : null,
      agent_system_prompt: agentPrompt.trim() || null,
    });
    onClose();
  };

  const isKbEnabled = selectedArticleIds.length > 0;
  const isPromptCustomized = agentPrompt.trim().length > 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content run-test-modal"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 680, width: '95vw' }}
      >
        {/* ── Header ── */}
        <div className="modal-header">
          <div>
            <h2>
              {ticketSubject.includes('Global') ? '📚 KB & Agent Config' :
               mode === 'batch' ? '🧪 Configure Batch Test' : '🧪 Configure Test'}
            </h2>
            {ticketSubject && (
              <p style={{ margin: '2px 0 0', fontSize: 13, opacity: 0.65 }}>
                {ticketSubject}
              </p>
            )}
          </div>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        {/* ── Tab bar ── */}
        <div className="run-test-tabs">
          <button
            className={`run-test-tab ${activeTab === 'kb' ? 'active' : ''}`}
            onClick={() => setActiveTab('kb')}
          >
            📚 Knowledge Base
            {isKbEnabled && (
              <span className="tab-badge">{selectedArticleIds.length}</span>
            )}
          </button>
          <button
            className={`run-test-tab ${activeTab === 'prompt' ? 'active' : ''}`}
            onClick={() => setActiveTab('prompt')}
          >
            ⚙️ Agent Prompt
            {isPromptCustomized && <span className="tab-badge">✓</span>}
          </button>
        </div>

        {/* ── Body ── */}
        <div className="modal-body run-test-modal-body">

          {/* ── KB Tab ── */}
          {activeTab === 'kb' && (
            <div className="run-test-kb-tab">
              <p className="run-test-description">
                Select the knowledge base articles the AI agent should use when
                answering customer questions. When articles are selected, a{' '}
                <strong>Groundedness</strong> metric will appear in results —
                scoring whether the agent cited the KB or hallucinated.
              </p>

              {loadingArticles ? (
                <div className="run-test-loading">Loading KB articles...</div>
              ) : kbArticles.length === 0 ? (
                <div className="run-test-empty-kb">
                  <p>No KB articles imported yet.</p>
                  <p className="hint">
                    Import articles from the KB tab first, then come back to
                    select them here.
                  </p>
                  <button
                    className="btn-secondary"
                    onClick={onClose}
                  >
                    Close and import articles
                  </button>
                </div>
              ) : (
                <>
                  {/* ── Collections row ── */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em' }}>
                        📁 COLLECTIONS
                      </span>
                      <button
                        onClick={() => setShowNewColl(v => !v)}
                        disabled={selectedArticleIds.length === 0}
                        title={selectedArticleIds.length === 0 ? 'Select articles first' : 'Save selection as a named collection'}
                        style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5, border: '1px solid #2d2d4e', background: 'transparent', color: selectedArticleIds.length > 0 ? '#a78bfa' : '#64748b', cursor: selectedArticleIds.length > 0 ? 'pointer' : 'not-allowed', fontWeight: 600 }}
                      >
                        + Save selection
                      </button>
                    </div>

                    {showNewColl && (
                      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                        <input
                          value={newCollName}
                          onChange={e => setNewCollName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && saveAsCollection()}
                          placeholder="Collection name..."
                          autoFocus
                          style={{ flex: 1, padding: '5px 9px', borderRadius: 5, border: '1px solid #7c3aed', background: '#0f0f23', color: '#e2e8f0', fontSize: 12, outline: 'none' }}
                        />
                        <button onClick={saveAsCollection} disabled={!newCollName.trim() || savingColl}
                          style={{ padding: '5px 12px', borderRadius: 5, border: 'none', background: '#7c3aed', color: 'white', fontSize: 12, cursor: 'pointer' }}>
                          {savingColl ? '...' : 'Save'}
                        </button>
                        <button onClick={() => setShowNewColl(false)}
                          style={{ padding: '5px 8px', borderRadius: 5, border: '1px solid #2d2d4e', background: 'transparent', color: '#64748b', fontSize: 12, cursor: 'pointer' }}>✕</button>
                      </div>
                    )}

                    {collections.length === 0 ? (
                      <div style={{ fontSize: 11, color: '#64748b', padding: '6px 10px', borderRadius: 5, border: '1px dashed #1e293b', textAlign: 'center' }}>
                        No collections yet — select articles and save them as a named collection.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {collections.map(coll => (
                          <button key={coll.id} onClick={() => applyCollection(coll)}
                            title={`${coll.article_ids.length} article${coll.article_ids.length !== 1 ? 's' : ''} — click to select all`}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 16, fontSize: 11, fontWeight: 500, cursor: 'pointer', border: '1px solid #2d2d4e', background: '#1a1a2e', color: '#e2e8f0' }}
                          >
                            📁 {coll.name}
                            <span style={{ fontSize: 10, color: '#64748b', background: '#0f0f23', borderRadius: 8, padding: '0 5px', border: '1px solid #1e293b' }}>{coll.article_ids.length}</span>
                            <span onClick={(e) => deleteCollection(coll.id, e)} title="Delete" style={{ fontSize: 10, color: '#64748b', cursor: 'pointer', marginLeft: 1 }}>✕</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="kb-select-actions">
                    <span className="kb-select-count">
                      {selectedArticleIds.length} of {kbArticles.length} selected
                    </span>
                    <button className="btn-link" onClick={selectAll}>Select all</button>
                    <button className="btn-link" onClick={clearAll}>Clear</button>
                  </div>

                  <div className="kb-article-list">
                    {(() => {
                      const grouped = {};
                      kbArticles.forEach(a => {
                        const cat = a.category_name || 'Articles';
                        const sec = a.section_name  || '';
                        if (!grouped[cat]) grouped[cat] = {};
                        if (!grouped[cat][sec]) grouped[cat][sec] = [];
                        grouped[cat][sec].push(a);
                      });
                      const multiCat = Object.keys(grouped).length > 1;
                      const multiSec = Object.values(grouped).some(s => Object.keys(s).filter(Boolean).length > 1);
                      const showGroups = multiCat || multiSec;
                      return Object.entries(grouped).map(([catName, sections]) => (
                        <div key={catName}>
                          {showGroups && multiCat && (
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', letterSpacing: '0.07em', padding: '10px 4px 4px', textTransform: 'uppercase' }}>
                              {catName}
                            </div>
                          )}
                          {Object.entries(sections).map(([secName, secArticles]) => {
                            const secIds = secArticles.map(a => String(a.id));
                            const allSel = secIds.every(id => selectedArticleIds.includes(id));
                            const someSel = !allSel && secIds.some(id => selectedArticleIds.includes(id));
                            const toggleSection = () => {
                              if (allSel) setSelectedArticleIds(prev => prev.filter(id => !secIds.includes(id)));
                              else setSelectedArticleIds(prev => [...new Set([...prev, ...secIds])]);
                            };
                            return (
                              <div key={secName || catName} style={{ marginBottom: 4 }}>
                                {showGroups && secName && (
                                  <div onClick={toggleSection}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#1a1a2e', borderRadius: '6px 6px 0 0', border: '1px solid #2d2d4e', cursor: 'pointer', userSelect: 'none' }}>
                                    <span style={{ fontSize: 13, color: allSel ? '#a78bfa' : someSel ? '#a78bfa' : '#64748b' }}>
                                      {allSel ? '☑' : someSel ? '⊟' : '☐'}
                                    </span>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', flex: 1 }}>{secName}</span>
                                    <span style={{ fontSize: 10, color: '#64748b' }}>{secIds.filter(id => selectedArticleIds.includes(id)).length}/{secArticles.length}</span>
                                  </div>
                                )}
                                <div style={{ border: showGroups && secName ? '1px solid #2d2d4e' : 'none', borderTop: 'none', borderRadius: showGroups && secName ? '0 0 6px 6px' : 0 }}>
                                  {secArticles.map((article, idx) => {
                                    const isSelected = selectedArticleIds.includes(String(article.id));
                                    const preview = (article.body || '').replace(/<[^>]+>/g, '').substring(0, 110);
                                    return (
                                      <div key={article.id}
                                        className={`kb-article-row ${isSelected ? 'selected' : ''}`}
                                        onClick={() => toggleArticle(article.id)}
                                      >
                                        <div className="kb-article-checkbox">{isSelected ? '☑' : '☐'}</div>
                                        <div className="kb-article-info">
                                          <div className="kb-article-title">{article.title}</div>
                                          {preview && <div className="kb-article-preview">{preview}{preview.length === 110 ? '…' : ''}</div>}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()}
                  </div>
                </>
              )}

              {!isKbEnabled && kbArticles.length > 0 && (
                <div className="run-test-no-kb-note">
                  ℹ️ Running without KB articles — the agent will use GPT-4's
                  general knowledge (no Groundedness metric).
                </div>
              )}
            </div>
          )}

          {/* ── Prompt Tab ── */}
          {activeTab === 'prompt' && (
            <div className="run-test-prompt-tab">
              <p className="run-test-description">
                Paste your Zendesk AI Agent's system prompt here. This is the
                persona, tone, and instruction set your deployed agent uses.
                If left blank, a sensible default is used.
              </p>

              <div className="prompt-template-row">
                <label>Start from a template:</label>
                <select
                  value={starterTemplate}
                  onChange={e => setStarterTemplate(e.target.value)}
                  className="prompt-template-select"
                >
                  {STARTER_PROMPTS.map(t => (
                    <option key={t.label} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <textarea
                className="agent-prompt-textarea"
                value={agentPrompt}
                onChange={e => setAgentPrompt(e.target.value)}
                placeholder={
                  'Paste your agent system prompt here...\n\n' +
                  'Example:\n' +
                  'You are Aria, a friendly support agent for Acme Corp.\n' +
                  'Always greet customers by name. Escalate billing disputes\n' +
                  'over $500 to the finance team. Never discuss competitors.'
                }
                rows={10}
              />
              <div className="prompt-char-count">
                {agentPrompt.length} characters
                {agentPrompt.length > 4000 && (
                  <span className="prompt-char-warning">
                    {' '}· Long prompts use more tokens per simulation
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="modal-footer run-test-modal-footer">
          <div className="run-test-config-summary">
            {isKbEnabled ? (
              <span className="config-tag config-tag-active">
                📚 {selectedArticleIds.length} KB article{selectedArticleIds.length !== 1 ? 's' : ''} · Groundedness enabled
              </span>
            ) : (
              <span className="config-tag config-tag-inactive">
                📚 No KB · Generic agent
              </span>
            )}
            {isPromptCustomized && (
              <span className="config-tag config-tag-active">
                ⚙️ Custom prompt
              </span>
            )}
          </div>
          <div className="run-test-modal-actions">
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
            <button className="btn-primary btn-run-test" onClick={handleConfirm}>
              {ticketSubject.includes('Global') ? '💾 Save Config' :
               mode === 'batch' ? '🧪 Run Batch Test' : '🧪 Run Test'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunTestModal;
